import { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Camera, WarningCircle, CheckCircle } from '@phosphor-icons/react'
import QrScanner from 'qr-scanner'
import { haptic } from '@/services/nativeService'
import { playScanBeep } from '@/utils/scanSound'

interface QRScannerSheetProps {
  isOpen: boolean
  onClose: () => void
  onCodeScanned: (code: string) => void
}

export default function QRScannerSheet({ isOpen, onClose, onCodeScanned }: QRScannerSheetProps) {
  const [error, setError] = useState<string | null>(null)
  const [isStarting, setIsStarting] = useState(true)
  const [showSuccess, setShowSuccess] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const scannerRef = useRef<QrScanner | null>(null)
  const hasScannedRef = useRef(false)

  useEffect(() => {
    if (!isOpen || !videoRef.current) return

    let mounted = true
    hasScannedRef.current = false
    setError(null)
    setIsStarting(true)
    setShowSuccess(false)

    const scanner = new QrScanner(
      videoRef.current,
      (result) => {
        if (hasScannedRef.current) return
        hasScannedRef.current = true

        // Ses + haptic feedback
        playScanBeep()
        haptic.success()

        // Başarı flash göster
        setShowSuccess(true)

        // URL'den davet kodunu çıkar veya ham metni kullan
        let code = result.data
        try {
          const url = new URL(result.data)
          const pathCode = url.pathname.split('/').pop()
          const paramCode = url.searchParams.get('code')
          code = paramCode || pathCode || result.data
        } catch {
          // URL değil — ham metin olarak kullan
        }

        // Kullanıcının başarı animasyonunu görmesi için kısa bekleme
        setTimeout(() => {
          scanner.destroy()
          scannerRef.current = null
          onCodeScanned(code)
          onClose()
        }, 400)
      },
      {
        preferredCamera: 'environment',
        maxScansPerSecond: 25,
        highlightScanRegion: false,
        highlightCodeOutline: false,
        returnDetailedScanResult: true,
        onDecodeError: () => {
          // QR bulunamadı — sessizce devam et
        },
      }
    )

    scannerRef.current = scanner

    scanner
      .start()
      .then(() => {
        if (mounted) setIsStarting(false)
      })
      .catch((err: any) => {
        if (!mounted) return
        setIsStarting(false)

        const msg = err?.message || err?.toString() || ''
        if (msg.includes('NotAllowedError') || msg.includes('Permission')) {
          setError('Kamera erişim izni reddedildi. Tarayıcı ayarlarından kamera iznini açabilirsiniz.')
        } else if (msg.includes('NotFoundError')) {
          setError('Cihazda kamera bulunamadı.')
        } else {
          setError('Kamera başlatılamadı. Lütfen başka bir tarayıcı deneyin.')
        }
      })

    return () => {
      mounted = false
      if (scannerRef.current) {
        scannerRef.current.destroy()
        scannerRef.current = null
      }
    }
  }, [isOpen, onCodeScanned, onClose])

  const handleClose = () => {
    if (scannerRef.current) {
      scannerRef.current.destroy()
      scannerRef.current = null
    }
    onClose()
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/60 z-[70]"
          />

          {/* Sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'tween', ease: 'easeOut', duration: 0.25 }}
            className="fixed bottom-0 left-0 right-0 bg-[#1A1A1A] rounded-t-3xl shadow-2xl z-[71] overflow-hidden max-h-[85vh] flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-4 pb-2">
              <div className="flex items-center gap-2">
                <Camera size={22} className="text-[#D4AF37]" weight="fill" />
                <h2 className="text-lg font-black text-white tracking-tight">QR Kod Tara</h2>
              </div>
              <button
                onClick={handleClose}
                className="p-2 text-gray-400 hover:text-white bg-white/10 rounded-full transition-colors"
                aria-label="Kapat"
              >
                <X size={18} weight="bold" />
              </button>
            </div>

            {/* Scanner Area */}
            <div className="flex-1 flex flex-col items-center justify-center px-6 pb-8 pt-2">
              {error ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col items-center gap-4 text-center py-8"
                >
                  <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center">
                    <WarningCircle size={36} className="text-red-400" weight="fill" />
                  </div>
                  <p className="text-red-300 text-sm font-medium max-w-[260px]">{error}</p>
                  <button
                    onClick={handleClose}
                    className="mt-2 px-6 py-3 bg-white/10 text-white font-bold text-sm rounded-xl hover:bg-white/20 transition-colors"
                  >
                    Geri Dön
                  </button>
                </motion.div>
              ) : (
                <div className="w-full max-w-[300px] relative">
                  {/* Viewfinder */}
                  <div className="relative rounded-2xl overflow-hidden border-2 border-[#D4AF37]/30 shadow-[0_0_40px_rgba(212,175,55,0.15)]">
                    {/* Video Element — qr-scanner renders camera stream here */}
                    <video
                      ref={videoRef}
                      className="w-full aspect-square object-cover"
                      playsInline
                      muted
                    />

                    {/* Shimmer skeleton — kamera başlarken */}
                    {isStarting && (
                      <div className="absolute inset-0 bg-[#1A1A1A] flex items-center justify-center">
                        <div className="w-full h-full relative overflow-hidden">
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-pulse" />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Camera size={48} className="text-[#D4AF37]/30" weight="fill" />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Başarı flash overlay */}
                    <AnimatePresence>
                      {showSuccess && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="absolute inset-0 bg-green-500/25 flex items-center justify-center backdrop-blur-[1px]"
                        >
                          <motion.div
                            initial={{ scale: 0.5, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ type: 'spring', damping: 15 }}
                          >
                            <CheckCircle size={64} className="text-green-400" weight="fill" />
                          </motion.div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Corner Markers */}
                    <div className="absolute inset-0 pointer-events-none">
                      {/* Top-Left */}
                      <div className="absolute top-3 left-3 w-8 h-8 border-t-3 border-l-3 border-[#D4AF37] rounded-tl-lg" />
                      {/* Top-Right */}
                      <div className="absolute top-3 right-3 w-8 h-8 border-t-3 border-r-3 border-[#D4AF37] rounded-tr-lg" />
                      {/* Bottom-Left */}
                      <div className="absolute bottom-3 left-3 w-8 h-8 border-b-3 border-l-3 border-[#D4AF37] rounded-bl-lg" />
                      {/* Bottom-Right */}
                      <div className="absolute bottom-3 right-3 w-8 h-8 border-b-3 border-r-3 border-[#D4AF37] rounded-br-lg" />
                    </div>

                    {/* Scan line animation */}
                    {!isStarting && !showSuccess && (
                      <motion.div
                        initial={{ top: '10%' }}
                        animate={{ top: '90%' }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          repeatType: 'reverse',
                          ease: 'easeInOut',
                        }}
                        className="absolute left-4 right-4 h-[2px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent shadow-[0_0_8px_rgba(212,175,55,0.6)] pointer-events-none"
                      />
                    )}
                  </div>

                  {/* Hint */}
                  <p className="text-center text-gray-400 text-xs font-medium mt-4">
                    {isStarting
                      ? 'Kamera başlatılıyor...'
                      : 'Davetiye kartındaki QR kodu çerçeveye getirin'}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
